'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { Button } from '@/app/components/ui/button';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';

// Only rendered on the China site, so the copy is not translated.
const FACE_CONSENT_COPY = {
  title: '生物识别信息授权声明',
  description: '当前功能涉及个人信息处理，操作前需先阅读并同意以下协议书。',
  tip: '请先打开并阅读以下协议书，勾选同意后才可继续使用。',
  agreePrefix: '我已阅读并同意',
  linkText: '《敏感个人信息收集处理授权同意书（面部识别特征）》',
  confirm: '已阅读并同意，继续操作',
};

interface LiveFaceConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function LiveFaceConsentModal({ open, onOpenChange, onConfirm }: LiveFaceConsentModalProps) {
  const t = useTranslations('vidu-stream.compliance');
  const [hasAgreed, setHasAgreed] = useState(false);

  useEffect(() => {
    if (open) {
      setHasAgreed(false);
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // The consent title needs ~600px to stay on one line.
        className="w-[min(600px,calc(100%-32px))] max-w-none sm:w-[calc(100%-24px)] sm:gap-3 sm:p-4"
        closable
        onClose={() => onOpenChange(false)}
        closeClassName="sm:top-4 sm:right-4"
        disableOverlayClose
      >
        <DialogHeader className="gap-2 sm:gap-1 sm:pr-10">
          <DialogTitle className="break-words text-left text-xl sm:text-base">{FACE_CONSENT_COPY.title}</DialogTitle>
          <DialogDescription className="break-words text-left text-sm leading-6 sm:text-xs sm:leading-5">
            {FACE_CONSENT_COPY.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-3">
          <div className="rounded-16 bg-system-white04 p-4 text-sm text-system-white64 leading-6 sm:p-3 sm:text-xs sm:leading-5">
            {FACE_CONSENT_COPY.tip}
          </div>

          <div className="flex items-start gap-3 rounded-12 border border-system-white08 bg-system-white04 p-4 sm:gap-2.5 sm:p-3">
            <Checkbox
              aria-label={FACE_CONSENT_COPY.linkText}
              className="mt-0.5 shrink-0 text-base text-system-white sm:mt-0"
              checked={hasAgreed}
              onCheckedChange={(checked) => setHasAgreed(Boolean(checked))}
            />
            <span className="min-w-0 flex-1 text-sm text-system-white leading-6 sm:text-xs sm:leading-5">
              {FACE_CONSENT_COPY.agreePrefix}
              <ClientLink
                className="mx-1 text-system-white underline underline-offset-4"
                href="/consent-face"
                rel="noreferrer"
                target="_blank"
              >
                {FACE_CONSENT_COPY.linkText}
              </ClientLink>
            </span>
          </div>

          <div className="flex flex-wrap justify-end gap-3 pt-2 sm:flex-col-reverse sm:items-stretch sm:gap-2 sm:pt-1">
            <Button variant="outline" size="dialog" className="sm:w-full" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              size="dialog"
              className="disabled:bg-none disabled:bg-system-neutral02 disabled:text-system-white24 disabled:opacity-100 sm:w-full"
              disabled={!hasAgreed}
              onClick={handleConfirm}
            >
              {FACE_CONSENT_COPY.confirm}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
